import React, { useEffect } from 'react';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from 'layout/Theme';
import Routes from 'routes/index';
import NavigationScroll from './NavigationScroll';
import OnboardLoading from '../component/base/onboard-loading-component/OnboardLoading';
import { useRecoilState } from 'recoil';
import { initInflightEventListener } from '../api/inflight/InflightService';
import { useLocation, useNavigate } from 'react-router-dom';
import OnboardDialogDynamic from '../component/base/onboard-dialog-dynamic-component/OnboardDialogDynamic';
import { useTranslation } from 'react-i18next';
import PanasonicLogManager from '../util/PanasonicLogManager';
import { useFonts } from '../util/CacheFont';
import { stopAllExcept, stopCInterval } from '../util/CustomSetInterval';
import OnboardConnectionError from '../component/base/onboard-connection-error-component/OnboardConnectionError';
import { connectionErrorAtom } from '../store/ConnectionErrorStore';
import InformationTab from '../component/information-tab/InformationTab';
import trFlag from '../assets/images/flags/tr.svg';
import enFlag from '../assets/images/flags/en.svg';

// ==============================|| APP ||============================== //
const App = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [connectionError, setConnectionError] = useRecoilState(connectionErrorAtom);
    const [rightPanelLang, setRightPanelLang] = React.useState(localStorage.getItem('rightPanelLang') || 'tr');
    const log = new PanasonicLogManager();

    useEffect(() => {
        initListener();
        useFonts();
    }, []);

    useEffect(() => {
        killAllIntervals();
    }, [location]);

    const killAllIntervals = () => {
        let pageName = window.location.hash.replace('#/', '');
        if (pageName.includes('/') > 0)
            pageName = pageName.split('/')[pageName.split('/').length - 1];
        if (pageName === 'remaining-usage') return;

        stopAllExcept('inflightListener');
    };

    useEffect(() => {
        document.documentElement.lang = i18n.language;
    }, [i18n.language]);

    useEffect(() => {
        localStorage.setItem('rightPanelLang', rightPanelLang);
    }, [rightPanelLang]);

    const initListener = () => {
        initInflightEventListener((status) => {
            stopCInterval('countdown');
            setConnectionError(!status);
        });
    };

    return (
        <div className="mobile-iframe-root">
            <div className={'mobile-iframe-menu' + (location.pathname === '/simulation' ? '-simulation' : '')}>
                <div className="mobile-iframe">
                    <NavigationScroll>
                        <StyledEngineProvider injectFirst>
                            <ThemeProvider theme={theme()}>
                                <CssBaseline />
                                <Routes log={log} initListener={initListener} />
                                <OnboardLoading />
                                <OnboardDialogDynamic />
                                <OnboardConnectionError />
                            </ThemeProvider>
                        </StyledEngineProvider>
                    </NavigationScroll>
                </div>
            </div>
            <div className="mobile-iframe-content-support"></div>
            <div className="mobile-iframe-content">
                <div className="mobile-iframe-content-sub">
                    <div className="mobile-iframe-header-row">
                        <h1>
                            <img alt="logo" src="/favicon.ico" />
                            {rightPanelLang === 'tr' ? 'Onboard Portal - Simülasyon' : 'Onboard Portal - Simulation'}
                        </h1>
                        <div className="right-panel-language-switch">
                            <button
                                className={rightPanelLang === 'tr' ? 'active' : ''}
                                onClick={() => setRightPanelLang('tr')}>
                                <img className="flag" src={trFlag} alt="Turkish" />
                                <span>TR</span>
                            </button>
                            <button
                                className={rightPanelLang === 'en' ? 'active' : ''}
                                onClick={() => setRightPanelLang('en')}>
                                <img className="flag" src={enFlag} alt="English" />
                                <span>EN</span>
                            </button>
                        </div>
                    </div>
                    <InformationTab rightPanelLang={rightPanelLang} />
                    <div
                        className={'mobile-iframe-content-footer' + (location.pathname === '/simulation' ? ' simulation' : '')}>
                        <div>
                            {(location.pathname === '/simulation') && (
                                <div className="mobile-iframe-content-simulation-info">
                                    <span className="status-dot"></span>
                                    <div>
                                        <b>{rightPanelLang === 'tr' ? 'Simülasyon Modu Aktif' : 'Simulation Mode Active'}</b>
                                        <p>{rightPanelLang === 'tr'
                                            ? 'Adımları sırayla takip ederek işlemi tamamlayın.'
                                            : 'Follow the steps in order to complete the flow.'}</p>
                                    </div>
                                </div>
                            )}
                            <div className="mobile-iframe-content-footer-btn">
                                {(location.pathname !== '/simulation') && (!connectionError && location.pathname !== '/') &&
                                    (<div className="mobile-iframe-content-back-button" onClick={() => navigate(-1)}>
                                        {rightPanelLang === 'tr' ? 'Önceki Sayfa' : 'Previous Page'}
                                    </div>)
                                }
                                {(location.pathname !== '/simulation') && (!connectionError && location.pathname === '/') &&
                                    (<div className="mobile-iframe-content-back-button"
                                          onClick={() => navigate('/login')}>
                                        {rightPanelLang === 'tr' ? 'Sonraki Sayfa' : 'Next Page'}
                                    </div>)
                                }
                                {(location.pathname !== '/simulation') && (
                                    <div
                                        className={'mobile-iframe-content-back-button ' + (!connectionError ? 'red' : 'green')}
                                        onClick={() => setConnectionError(!connectionError)}>
                                        {!connectionError
                                            ? (rightPanelLang === 'tr' ? 'Hata Çıkar' : 'Trigger Error')
                                            : (rightPanelLang === 'tr' ? 'Hata Kapat' : 'Close Error')}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div>
                            <img className="mobile-iframe-content-footer-logo" alt="logo"
                                 src="/images/logo-turkishairlines.svg" width={176} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default App;
